'use client';

import { UploadCloud } from 'lucide-react';

import { ChangeEvent, DragEvent, FormEvent, useMemo, useRef, useState } from 'react';
import { CapSignupCta } from '@/components/free-tools/cap-signup-cta';
import Link from 'next/link';

import { FREE_AUDIO_TO_TEXT_FAQS } from './faqs';
import { FREE_AUDIO_TO_TEXT_WATERMARK } from '@/lib/free-tools/audio-copy';
import { MAX_AUDIO_BYTES, isAllowedAudioExtension } from '@engine/tools-shell';

import {
  completePublicUpload,
  initPublicUpload,
  uploadPublicChunks,
} from '@/lib/free-tools/public-chunked-uploader';
import { Badge } from '@/components/badge';
import {
  FreeToolTranscribeCta,
  trackFreeToolTranscribeIntent,
  useFreeToolTranscribeHandoffLinks,
} from '@/components/free-tools/free-tool-transcribe-cta';
import { Progress } from '@/components/progress';
import { cn } from '@/lib/utils';

type JobStatus = 'queued' | 'pending' | 'processing' | 'completed' | 'failed';

interface SubmitResponse {
  success: boolean;
  data?: { jobId: string; publicToken: string; handoffToken?: string };
  error?: string;
}

interface JobResponse {
  success: boolean;
  data?: {
    id: string;
    status: JobStatus;
    fileName: string;
    transcriptText: string;
    durationSeconds: number;
    wordCount: number;
    errorMessage: string | null;
  };
  error?: string;
}

const ACCEPT = '.mp4,.mp3,.wav,.m4a,.ogg,.webm,.flac,.aac,.mpeg,.mpga';

const BLOCKED_MIME_PREFIXES = ['image/'];

function formatDuration(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;

  if (hours > 0) return `${hours}h ${minutes}m ${secs}s`;

  if (minutes > 0) return `${minutes}m ${secs}s`;

  return `${secs}s`;
}

function getFriendlySubmitError(message: string | null | undefined): string {
  if (!message) return 'We could not start transcription right now. Please try again.';
  const lower = message.toLowerCase();

  if (
    lower.includes('unexpected end of form') ||
    lower.includes('multipart') ||
    lower.includes('content-type')
  ) {
    return 'Upload was interrupted. Please reselect your file and try again.';
  }

  if (lower.includes('unsupported file type')) {
    return 'Unsupported file type. Use MP3, WAV, M4A, OGG, WEBM, or FLAC.';
  }

  if (lower.includes('500mb') || lower.includes('file size')) {
    return 'File size exceeds 500MB free limit.';
  }

  if (lower.includes('no file was received') || lower.includes('no file was fully uploaded')) {
    return 'No file was fully uploaded. Please reselect your file and try again.';
  }

  if (lower.includes('request body exceeded') || lower.includes('exceeded 10mb')) {
    return 'File is too large for upload. Keep it under 500MB and try again.';
  }

  if (lower.includes('session expired')) {
    return 'Upload session expired. Please reselect your file and try again.';
  }

  return message;
}

export interface FreeAudioToTextCopy {
  /** Page H1. */
  h1?: string;
  /** Intro paragraph under the H1. */
  intro?: string;
  /** Drop-zone heading, e.g. "Drag and drop your video file". */
  dropHeading?: string;
}

const DEFAULT_COPY: Required<FreeAudioToTextCopy> = {
  h1: 'Free Audio-to-Text Converter',
  intro:
    'Convert audio to text free online in minutes. Upload MP3, WAV, M4A, OGG, WEBM, or FLAC with no signup required for files up to 10 minutes.',
  dropHeading: 'Drag and drop your audio file',
};

export function FreeAudioToTextClient({ copy }: { copy?: FreeAudioToTextCopy } = {}) {
  const text = { ...DEFAULT_COPY, ...copy };
  const [file, setFile] = useState<File | null>(null);
  const [inputUrl, setInputUrl] = useState('');
  const [isDragActive, setIsDragActive] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [publicToken, setPublicToken] = useState<string | null>(null);
  const [handoffToken, setHandoffToken] = useState<string | null>(null);
  const [status, setStatus] = useState<JobStatus | null>(null);
  const [transcriptText, setTranscriptText] = useState('');
  const [wordCount, setWordCount] = useState(0);
  const [durationSeconds, setDurationSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const progressValue = useMemo(() => {
    if (status === 'queued') return 10;

    if (status === 'pending') return 20;

    if (status === 'processing' && uploadProgress > 0 && uploadProgress < 100) {
      return Math.max(20, Math.min(60, Math.round(uploadProgress * 0.6)));
    }

    if (status === 'processing') return 65;

    if (status === 'completed') return 100;

    if (status === 'failed') return 0;

    return 0;
  }, [status, uploadProgress]);

  const canSubmit = Boolean(file || inputUrl.trim().length > 0) && !submitting;
  const isTranscriptReady = status === 'completed' && Boolean(transcriptText);
  const showUploader = inputUrl.trim().length === 0;
  const showLinkInput = !file;

  const { primaryHref: transcribeHandoffHref, ready: handoffLinksReady } =
    useFreeToolTranscribeHandoffLinks(jobId, handoffToken);

  function isAllowedAudioFile(nextFile: File): boolean {
    if (!isAllowedAudioExtension(nextFile.name)) return false;
    const mimeType = nextFile.type.toLowerCase();

    if (BLOCKED_MIME_PREFIXES.some((prefix) => mimeType.startsWith(prefix))) return false;

    return true;
  }

  function handleSelectFile(nextFile: File | null): void {
    if (nextFile && !isAllowedAudioFile(nextFile)) {
      setFile(null);
      setError('Unsupported file type. Use MP3, WAV, M4A, OGG, WEBM, or FLAC.');

      return;
    }

    if (nextFile && nextFile.size > MAX_AUDIO_BYTES) {
      setFile(null);
      setError('File size exceeds 500MB free limit.');

      return;
    }

    setFile(nextFile);

    if (nextFile) {
      setError(null);
      setInputUrl('');
    }
  }

  function handleFileChange(event: ChangeEvent<HTMLInputElement>): void {
    const nextFile = event.target.files?.[0] ?? null;
    handleSelectFile(nextFile);
  }

  function openFilePicker(): void {
    fileInputRef.current?.click();
  }

  function handleDrop(event: DragEvent<HTMLDivElement>): void {
    event.preventDefault();
    setIsDragActive(false);
    const droppedFile = event.dataTransfer.files?.[0] ?? null;

    if (droppedFile) {
      handleSelectFile(droppedFile);
    }
  }

  function handleLinkChange(event: ChangeEvent<HTMLInputElement>): void {
    const nextUrl = event.target.value;
    setInputUrl(nextUrl);

    if (nextUrl.trim().length > 0) {
      setError(null);
      setFile(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }

  async function pollJob(nextJobId: string, token: string): Promise<void> {
    for (let i = 0; i < 120; i += 1) {
      const response = await fetch(
        `/api/tools/free-audio-to-text/jobs/${nextJobId}?token=${encodeURIComponent(token)}`,
        { method: 'GET', cache: 'no-store' }
      );

      // SAFETY: The surrounding typed contract establishes the asserted representation.
      const payload = (await response.json()) as JobResponse;

      if (!response.ok || !payload.success || !payload.data) {
        setError(payload.error ?? 'Failed to fetch transcription status');
        setStatus('failed');

        return;
      }

      setStatus(payload.data.status);
      setTranscriptText(payload.data.transcriptText || '');
      setWordCount(payload.data.wordCount || 0);
      setDurationSeconds(payload.data.durationSeconds || 0);
      setError(payload.data.errorMessage ?? null);

      if (payload.data.status === 'completed' || payload.data.status === 'failed') {
        return;
      }

      await new Promise((resolve) => setTimeout(resolve, 2000));
    }

    setStatus('failed');
    setError('Transcription took too long. Please try again.');
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!canSubmit) return;

    setSubmitting(true);
    setError(null);
    setStatus('pending');
    setTranscriptText('');
    setWordCount(0);
    setDurationSeconds(0);
    setCopyState('idle');
    setJobId(null);
    setPublicToken(null);
    setHandoffToken(null);
    setUploadProgress(0);

    try {
      let submitData: SubmitResponse['data'] | undefined;

      if (file) {
        const session = await initPublicUpload(file);
        await uploadPublicChunks(file, session, setUploadProgress);
        submitData = await completePublicUpload(session);
      } else {
        const formData = new FormData();

        if (inputUrl.trim()) formData.set('inputUrl', inputUrl.trim());

        const response = await fetch('/api/tools/free-audio-to-text/jobs', {
          method: 'POST',
          body: formData,
        });

        // SAFETY: The surrounding typed contract establishes the asserted representation.
        const payload = (await response.json()) as SubmitResponse;

        if (!response.ok || !payload.success || !payload.data) {
          setStatus('failed');
          setError(getFriendlySubmitError(payload.error));

          return;
        }

        submitData = payload.data;
      }

      if (!submitData) {
        setStatus('failed');
        setError('We could not start transcription right now. Please try again.');

        return;
      }

      setJobId(submitData.jobId);
      setPublicToken(submitData.publicToken);
      setHandoffToken(submitData.handoffToken ?? submitData.publicToken);
      setStatus('processing');
      await pollJob(submitData.jobId, submitData.publicToken);
    } catch (submitError) {
      setStatus('failed');
      setError(
        getFriendlySubmitError(
          submitError instanceof Error ? submitError.message : 'Unexpected error'
        )
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCopyTranscript() {
    if (!transcriptText) return;

    try {
      await navigator.clipboard.writeText(`${transcriptText}\n\n${FREE_AUDIO_TO_TEXT_WATERMARK}`);
      setCopyState('copied');
      window.setTimeout(() => setCopyState('idle'), 2000);
    } catch {
      setCopyState('idle');
    }
  }

  function handleTranscribeAgain(): void {
    setFile(null);
    setInputUrl('');
    setIsDragActive(false);
    setJobId(null);
    setPublicToken(null);
    setHandoffToken(null);
    setStatus(null);
    setTranscriptText('');
    setWordCount(0);
    setDurationSeconds(0);
    setError(null);
    setSubmitting(false);
    setCopyState('idle');
    setUploadProgress(0);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }

  return (
    <div className="space-y-6">
      {/* H1 and intro live in FreeToolPageShell; only live status stays here. */}

      {!isTranscriptReady ? (
        <form
          onSubmit={handleSubmit}
          className="space-y-4 rounded-[28px] border border-[rgb(var(--surface-border))] bg-[rgb(var(--surface))] p-5 sm:p-6"
        >
          {showUploader ? (
            <div className="space-y-2">
              <label className="block text-sm font-medium" htmlFor="audio-file">
                Upload audio file
              </label>
              <input
                id="audio-file"
                ref={fileInputRef}
                type="file"
                accept={ACCEPT}
                onChange={handleFileChange}
                className="sr-only"
              />
              <div
                role="button"
                tabIndex={0}
                onClick={openFilePicker}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openFilePicker();
                  }
                }}
                onDragOver={(event) => {
                  event.preventDefault();
                  setIsDragActive(true);
                }}
                onDragLeave={() => setIsDragActive(false)}
                onDrop={handleDrop}
                className={cn(
                  'flex min-h-36 w-full cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed px-4 py-5 text-center transition-colors',
                  isDragActive
                    ? 'border-violet-400/70 bg-violet-500/10'
                    : 'border-[rgb(var(--surface-border))] bg-[rgb(var(--background))] hover:border-violet-400/70 hover:bg-[rgb(var(--surface-hover))]'
                )}
              >
                <UploadCloud aria-hidden className="mb-3 h-7 w-7 text-[#C4B5FD]" />
                <p className="text-base font-semibold text-[rgb(var(--foreground))]">
                  {text.dropHeading}
                </p>
                <p className="mt-1 text-sm text-[rgb(var(--foreground-secondary))]">
                  or click to browse files
                </p>
                <p className="mt-4 text-xs text-[rgb(var(--foreground-secondary))]">
                  Supported: MP3, WAV, M4A, OGG, WEBM, FLAC. Max 10 minutes free / 500MB.
                </p>
              </div>
            </div>
          ) : null}

          {file ? (
            <div className="rounded-lg border border-violet-400/25 bg-[rgb(var(--surface))] p-3">
              <p className="text-xs text-[rgb(var(--foreground-secondary))]">Selected file</p>
              <p className="mt-1 break-all text-sm font-medium text-[rgb(var(--foreground))]">
                {file.name}
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={openFilePicker}
                  className="inline-flex items-center justify-center rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-white/15"
                >
                  Change file
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setFile(null);

                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  className="inline-flex items-center justify-center rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-white/15"
                >
                  Clear
                </button>
              </div>
            </div>
          ) : null}

          {showLinkInput ? (
            <details className="pt-2">
              <summary className="cursor-pointer text-sm font-medium text-[rgb(var(--foreground-secondary))]">
                Use a link instead
              </summary>
              <label className="block text-sm font-medium" htmlFor="audio-link">
                Or paste a YouTube or direct media link
              </label>
              <input
                id="audio-link"
                type="url"
                placeholder="https://youtube.com/watch?v=... or https://example.com/file.mp3"
                value={inputUrl}
                onChange={handleLinkChange}
                className="mt-2 w-full rounded-lg border border-[rgb(var(--surface-border))] bg-[rgb(var(--background))] px-4 py-3 text-sm"
              />
            </details>
          ) : null}

          <button
            type="submit"
            disabled={!canSubmit}
            className="mt-3 inline-flex items-center justify-center rounded-full bg-[rgb(var(--tool-cta))] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[rgb(var(--tool-cta-hover))] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? 'Transcribing...' : 'Transcribe Audio'}
          </button>
        </form>
      ) : null}
      <details className="text-sm text-[rgb(var(--foreground-secondary))]">
        <summary className="mx-auto w-fit cursor-pointer">About this tool</summary>
        <div className="space-y-4">
          <p className="text-[rgb(var(--foreground-secondary))]">{text.intro}</p>
          <p className="text-sm text-[rgb(var(--foreground-secondary))]">
            Need team workflows after conversion? Continue in{' '}
            <Link href="/features" className="underline underline-offset-2">
              Wisprs features
            </Link>{' '}
            or compare plans on{' '}
            <Link href="/pricing" className="underline underline-offset-2">
              pricing
            </Link>
            .
          </p>
        </div>
      </details>

      {status ? (
        <div className="mt-6 rounded-xl border border-[rgb(var(--surface-border))] bg-[rgb(var(--surface))] p-5">
          {status !== 'failed' ? (
            <Progress value={progressValue} showLabel className="mb-3" />
          ) : null}
          {status === 'failed' ? (
            <div className="space-y-1">
              <p className="text-sm font-semibold text-[rgb(var(--error))]">
                Transcription could not start
              </p>
              <p className="text-sm text-[rgb(var(--foreground-secondary))]">
                {error ?? 'Please try again.'}
              </p>
              <p className="text-xs text-[rgb(var(--foreground-secondary))]">
                Upload may have completed, but anonymous free mode can only transcribe supported
                files up to 10 minutes. Try reselecting the file and submitting again.
              </p>
              <CapSignupCta error={error} slug="free-audio-to-text" />
            </div>
          ) : (
            <p className="text-sm text-[rgb(var(--foreground-secondary))]">
              {status === 'queued' && 'Upload queued. Waiting for worker...'}
              {status === 'pending' && 'Upload queued. Preparing transcription...'}
              {status === 'processing' && 'Transcribing your audio...'}
              {status === 'completed' && 'Transcription completed.'}
            </p>
          )}
          {jobId && publicToken ? (
            <p className="mt-2 font-mono text-[11px] text-[rgb(var(--foreground-secondary))]">
              Job {jobId}
            </p>
          ) : null}
          {error && status !== 'failed' ? (
            <>
              <p className="mt-2 text-sm text-[rgb(var(--error))]">{error}</p>
              <CapSignupCta error={error} slug="free-audio-to-text" />
            </>
          ) : null}
        </div>
      ) : null}

      {isTranscriptReady ? (
        <div className="mt-8 space-y-4 rounded-xl border border-[rgb(var(--surface-border))] bg-[rgb(var(--surface))] p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-semibold">Transcript</h2>
            <button
              type="button"
              onClick={handleCopyTranscript}
              className="inline-flex min-h-10 items-center justify-center rounded-full border border-white/20 bg-white/10 px-4 text-sm font-semibold text-white transition-colors hover:bg-white/15"
            >
              {copyState === 'copied' ? 'Copied' : 'Copy transcript'}
            </button>
          </div>
          <p className="text-xs text-[rgb(var(--foreground-secondary))]">
            {wordCount} words · {formatDuration(durationSeconds)}
          </p>
          <textarea
            readOnly
            value={transcriptText}
            rows={12}
            className="min-h-80 max-h-[70vh] w-full resize-y rounded-lg border border-[rgb(var(--surface-border))] bg-[rgb(var(--background))] p-4 text-sm leading-6"
          />
          <p className="text-xs text-[rgb(var(--foreground-secondary))]">
            {FREE_AUDIO_TO_TEXT_WATERMARK}
          </p>
          <p className="text-sm text-[rgb(var(--foreground-secondary))]">
            Want summaries, exports, speaker tools, or longer-file workflows? Import this transcript
            into Wisprs.
          </p>
          <div className="flex flex-wrap gap-3">
            <FreeToolTranscribeCta
              jobId={jobId}
              downloadToken={handoffToken}
              freeToolKind="free-audio-to-text"
              className="w-auto px-5"
            >
              Extract AI Summary on Wisprs
            </FreeToolTranscribeCta>
            <button
              type="button"
              onClick={handleTranscribeAgain}
              className="inline-flex min-h-11 items-center justify-center rounded-full border border-white/20 bg-white/10 px-5 text-sm font-semibold text-white transition-colors hover:bg-white/15"
            >
              Transcribe again
            </button>
          </div>
          <p className="text-xs text-[rgb(var(--foreground-secondary))]">
            Already have an account?{' '}
            <Link
              href={handoffLinksReady ? transcribeHandoffHref : '/sign-in'}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-[rgb(var(--foreground))] underline underline-offset-2"
              onClick={() => {
                if (jobId && handoffLinksReady) {
                  trackFreeToolTranscribeIntent('free-audio-to-text', jobId);
                }
              }}
            >
              Import this transcript
            </Link>{' '}
            to continue in the dashboard.
          </p>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Long-form SEO cards for this tool. Rendered by the page through FreeToolPageShell's `body`
 * slot, below the hero, so the tool, its "About"/"How this works" panel and the platform
 * chips stay together in the first view.
 */
export function FreeAudioToTextSeoSections() {
  return (
    <>
      <div className="grid gap-4 md:grid-cols-2">
        <section className="rounded-xl border border-violet-400/20 bg-[rgb(var(--surface))] p-5">
          <h2 className="text-base font-semibold">How this free audio-to-text tool works</h2>
          <ol className="mt-3 list-decimal space-y-1 pl-4 text-sm text-[rgb(var(--foreground-secondary))]">
            <li>Upload an audio file or paste a direct audio URL.</li>
            <li>Pick Speed or Best Quality, then start transcription.</li>
            <li>
              Read the transcript in-page, copy it, or import to Wisprs for AI summaries and
              exports.
            </li>
          </ol>
          <p className="mt-3 text-sm text-[rgb(var(--foreground-secondary))]">
            The free flow runs Whisper-based models with automatic language detection across 100+
            languages, so there is nothing to configure before you upload.
          </p>
        </section>
        <section className="rounded-xl border border-violet-400/20 bg-[rgb(var(--surface))] p-5">
          <h2 className="text-base font-semibold">Supported formats and limits</h2>
          <p className="mt-3 text-sm text-[rgb(var(--foreground-secondary))]">
            Supported formats: MP3, WAV, M4A, OGG, WEBM, FLAC. Free usage covers files up to 10
            minutes and 500MB, with free TXT and SRT exports. Richer exports like DOCX, VTT, and
            JSON come with the Pro, Studio, and Agency plans. For long-form files, speaker
            workflows, and reusable content operations, use{' '}
            <Link
              href="/use-cases/meeting-transcription-software"
              className="underline underline-offset-2"
            >
              meeting transcription software
            </Link>{' '}
            and{' '}
            <Link
              href="/podcast/podcast-transcription-service"
              className="underline underline-offset-2"
            >
              podcast transcription workflows
            </Link>
            .
          </p>
        </section>
      </div>

      <div className="mt-10 space-y-6">
        <section className="space-y-3 rounded-xl border border-violet-400/20 bg-[rgb(var(--surface))] p-6">
          <h2 className="text-lg font-semibold text-[rgb(var(--foreground))]">
            What you can expect from the free flow
          </h2>
          <p className="text-sm leading-6 text-[rgb(var(--foreground-secondary))]">
            The free version is built for short files, quick checks, and occasional use rather than
            heavy production. Most short recordings finish within a few minutes, depending on queue
            load and the mode you choose. Speed mode returns results faster with slightly lower
            accuracy, while Best Quality takes a little longer but produces cleaner text.
          </p>
          <p className="text-sm leading-6 text-[rgb(var(--foreground-secondary))]">
            A few tradeoffs are worth knowing. The free tier has no speaker labels, so multi-speaker
            recordings arrive as a single block of text, and exports may carry a small watermark.
            You get excellent accuracy on clear audio, while results vary by language, accent, and
            recording quality.
          </p>
        </section>
        <section className="space-y-3 rounded-xl border border-violet-400/20 bg-[rgb(var(--surface))] p-6">
          <h2 className="text-lg font-semibold text-[rgb(var(--foreground))]">
            When it makes sense to upgrade
          </h2>
          <p className="text-sm leading-6 text-[rgb(var(--foreground-secondary))]">
            Upgrading pays off when transcription becomes part of your regular workflow rather than
            a one-off task. Speaker identification is the biggest single win for interviews,
            meetings, and multi-voice podcasts, since Wisprs separates speakers automatically and
            saves real editing time on longer recordings.
          </p>
          <p className="text-sm leading-6 text-[rgb(var(--foreground-secondary))]">
            Paid tiers also add export formats like DOCX, VTT, and JSON, higher-tier engines, and
            batch processing for many files at once. If you are comparing tools first, the free
            version is a realistic preview of speed and baseline quality. Compare what each tier
            adds on{' '}
            <Link href="/pricing" className="underline underline-offset-2">
              pricing
            </Link>
            .
          </p>
        </section>
      </div>
    </>
  );
}
